/**
 * How old a reading is, and whether that is a problem yet.
 *
 * Two rules from the plan, both load-bearing:
 *
 *   * Staleness is measured against the institution's own `as_of`, never
 *     against when our job happened to run. `balance_observation.as_of` says
 *     the same thing in SQL.
 *   * A number without its age invites a decision on a partial picture. So
 *     nothing in this app renders a balance without calling into here.
 *
 * The age shown is anchored on the server's own `now() - as_of`, taken at the
 * moment the response was received, and advanced by the client clock from
 * there. That is deliberate: it survives a workstation whose clock is eleven
 * minutes fast, which reading `as_of` against `Date.now()` does not, while
 * still ticking upward on a page somebody left open over lunch.
 */

/** The institution default from 001_registry.sql. Overridden per institution. */
export const DEFAULT_STALENESS_WARN_MINUTES = 240;

export type Freshness = "fresh" | "stale" | "unknown";

const MINUTE = 60;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/**
 * Postgres intervals arrive from FastAPI as a number of seconds
 * (`jsonable_encoder` turns a timedelta into `total_seconds()`).
 * Anything else -- a null age on an account that has never been read -- is
 * not an age of zero and must not be treated as one.
 */
export function ageSeconds(
  serverAgeSeconds: number | null | undefined,
  receivedAtMs: number,
): number | null {
  if (serverAgeSeconds === null || serverAgeSeconds === undefined) return null;
  const elapsed = (Date.now() - receivedAtMs) / 1000;
  return serverAgeSeconds + Math.max(0, elapsed);
}

/**
 * "4 minutes ago". Never "just now" -- a reading that is nine seconds old is
 * nine seconds old, and rounding that away is the first step towards a screen
 * that stops saying how old anything is.
 */
export function formatAge(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return "never read";
  if (seconds < 0) return "clock skew";

  const whole = Math.floor(seconds);
  if (whole < MINUTE) return `${whole}s ago`;
  if (whole < HOUR) {
    const minutes = Math.floor(whole / MINUTE);
    return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
  }
  if (whole < DAY) {
    const hours = Math.floor(whole / HOUR);
    const minutes = Math.floor((whole % HOUR) / MINUTE);
    if (hours < 6 && minutes > 0) return `${hours}h ${minutes}m ago`;
    return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  }
  const days = Math.floor(whole / DAY);
  return `${days} day${days === 1 ? "" : "s"} ago`;
}

/** Short form for a dense table cell. "4m", "2h", "3d". */
export function formatAgeShort(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return "—";
  if (seconds < 0) return "skew";
  const whole = Math.floor(seconds);
  if (whole < MINUTE) return `${whole}s`;
  if (whole < HOUR) return `${Math.floor(whole / MINUTE)}m`;
  if (whole < DAY) return `${Math.floor(whole / HOUR)}h`;
  return `${Math.floor(whole / DAY)}d`;
}

/**
 * Whose clock the age is measured on, said in one word.
 *
 * `balance_observation.as_of` is NOT NULL and Plaid reports
 * `last_updated_datetime` for some institutions and omits it for others, so
 * the read time stands in whenever the bank says nothing -- which on this book
 * is every institution, 0 of 65 accounts across the two largest logins. 019
 * records which of the two it was in `as_of_reported`; until this, nothing
 * between that column and the pixel carried it, so every screen presented our
 * own clock as the bank's.
 *
 * One word, not a sentence. "read 4 minutes ago" says the age is a lower bound
 * on staleness without putting a parenthetical beside every balance on seven
 * pages. The rest goes in `UNREPORTED_AGE_NOTE`, on the `title` the age cell
 * already sets.
 *
 * When the bank DOES report its own timestamp the age is real staleness and
 * needs no qualifier, so the wording is exactly what it has always been. That
 * branch fires zero times on this book and is one line on purpose.
 *
 * Never read is never read: there is no clock to attribute and the qualifier
 * would be attached to nothing.
 */
export function ageWithSource(
  seconds: number | null | undefined,
  asOfReported: boolean | null | undefined,
  short = false,
): string {
  const text = short ? formatAgeShort(seconds) : formatAge(seconds);
  if (seconds === null || seconds === undefined) return text;
  if (seconds < 0) return text;
  return asOfReported ? text : `read ${text}`;
}

/** Why the age says "read". Goes on a `title`, never on the face of a screen. */
export const UNREPORTED_AGE_NOTE =
  "This institution does not report when it last updated the figure, so this " +
  "is when we read it. The balance may be older.";

/**
 * The hard visual break. Past the institution's threshold a reading is stale,
 * and stale is a state of its own -- not a slightly-greyer version of fresh.
 * An account never read is `unknown`, which is also not fresh.
 */
export function freshness(
  seconds: number | null | undefined,
  warnMinutes: number | null | undefined = DEFAULT_STALENESS_WARN_MINUTES,
): Freshness {
  if (seconds === null || seconds === undefined) return "unknown";
  const threshold = (warnMinutes ?? DEFAULT_STALENESS_WARN_MINUTES) * MINUTE;
  return seconds > threshold ? "stale" : "fresh";
}

/**
 * The wall-clock time a reading was taken, from its server-computed age.
 *
 * Sarah, on her first look at the dashboard: *"under refresh, add a date and
 * time stamp of when the last reading was."* Everything in this app carries an
 * age and nothing carried an absolute time, which is fine for "4 minutes ago"
 * and useless for deciding whether the button you just pressed did anything.
 *
 * Derived rather than sent: the server gives `now() - as_of` in seconds and
 * `receivedAtMs` is when that answer landed, so the subtraction lands on the
 * same instant `formatAge` is already counting from. Anchoring it here rather
 * than on `Date.now()` keeps the promise the rest of this module makes -- a
 * workstation with a wrong clock shows the same reading time as a right one,
 * because both are measured from the server's own view of when it happened.
 *
 * Null in, null out. An account never read has no reading time, and rendering
 * one as the epoch or as "now" is the same lie as an age of zero.
 */
export function readingTakenAt(
  serverAgeSeconds: number | null | undefined,
  receivedAtMs: number | null,
): Date | null {
  if (serverAgeSeconds === null || serverAgeSeconds === undefined) return null;
  if (receivedAtMs === null) return null;
  return new Date(receivedAtMs - serverAgeSeconds * 1000);
}

/** `readingTakenAt`, formatted the same way `formatTimestamp` formats an ISO string. */
export function formatReadingTakenAt(
  serverAgeSeconds: number | null | undefined,
  receivedAtMs: number | null,
): string {
  const at = readingTakenAt(serverAgeSeconds, receivedAtMs);
  if (at === null || Number.isNaN(at.getTime())) return "never read";
  return at.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

/** An absolute timestamp, for the places where "2 hours ago" is not enough. */
export function formatTimestamp(iso: string | null | undefined): string {
  if (!iso) return "—";
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return "—";
  return at.toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * How long ago a timestamp was, from the timestamp itself.
 *
 * `formatAge` takes an age in seconds the server computed. Feed sync times
 * arrive as ISO timestamps instead, because unlike a balance reading they are
 * not compared against a per-institution threshold — nothing has ever decided
 * how stale a transaction feed is allowed to be, and inventing a threshold
 * here would be inventing a policy.
 *
 * `null` means never pulled, which is a different and stronger statement than
 * "old". It must not render as a duration.
 */
export function formatSince(iso: string | null | undefined): string {
  if (!iso) return "never pulled";
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return "unknown";
  return formatAge((Date.now() - then) / 1000);
}

/**
 * Today, as the date the BANK would post it under, in `YYYY-MM-DD`.
 *
 * ⚠️ Local time, never UTC. `toISOString().slice(0, 10)` is the obvious
 * one-liner and it is wrong for exactly the people who use this: from New York
 * it returns TOMORROW's date after 20:00 (19:00 in winter), so a screen
 * defaulting to "today" would quietly show an empty day every single evening
 * and read as "no money moved".
 *
 * `en-CA` is used because its short date format is already `YYYY-MM-DD`, which
 * is what `bank_transaction.posted_on` and the API's `since`/`until` take.
 */
export function todayISO(now: Date = new Date()): string {
  return now.toLocaleDateString("en-CA");
}
